import { useMemo, useState } from 'react';
import { Button, Input, Popover, Tooltip } from 'antd';
import { SpaceAvatar } from './SpaceAvatar.tsx';
import { SPACE_ICONS, resolveSpaceIconKey, resolveSpaceColor, buildSpaceIcon, getSpaceIconDef } from './spaceIcons.ts';
import { spaceTilePalette } from '../theme/tokens.ts';

/**
 * Пикер иконки пространства: курируемый набор AntD-иконок.
 *
 * Значение хранится в одном строковом поле `icon` (без изменения схемы):
 *  - выбор иконки из реестра → `'i:<key>'`;
 *  - «убрать» → `null` (тогда аватар = буквенная монограмма).
 *
 * Эмодзи как системные глифы больше не предлагаем (серьёзный тон): аватар
 * рендерит только иконку из реестра либо монограмму, поэтому пикер показывает
 * ровно то, что появится в рельсе/на home — «что выбрал, то и видишь».
 * Стили на var(--color-*) — читаются в светлой и тёмной теме.
 */

interface SpaceIconPickerProps {
  value?: string | null;
  onChange?: (icon: string | null) => void;
  /** Название пространства — для превью (монограмма) и цвета плитки. */
  name?: string;
  /**
   * Режим «только иконка» — для представлений/типов записей: без выбора цвета
   * и без цветной плитки; значение = `i:<key>` (иконка рендерится inline).
   */
  iconOnly?: boolean;
}

export function SpaceIconPicker({
  value,
  onChange,
  name = '',
  iconOnly = false,
}: SpaceIconPickerProps): JSX.Element {
  const selectedIconKey = useMemo(() => resolveSpaceIconKey(value), [value]);
  const selectedColor = useMemo(() => resolveSpaceColor(value), [value]);
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const items = q
      ? SPACE_ICONS.filter((d) => d.label.toLowerCase().includes(q) || d.key.includes(q) || d.group.toLowerCase().includes(q))
      : SPACE_ICONS;
    const order: string[] = [];
    const byGroup = new Map<string, typeof SPACE_ICONS>();
    for (const def of items) {
      if (!byGroup.has(def.group)) {
        byGroup.set(def.group, []);
        order.push(def.group);
      }
      byGroup.get(def.group)!.push(def);
    }
    return order.map((g) => ({ group: g, items: byGroup.get(g)! }));
  }, [query]);

  const resetBtn =
    selectedIconKey || selectedColor ? (
      <Button
        type="link"
        size="small"
        style={{ padding: 0, height: 'auto' }}
        onClick={() => onChange?.(null)}
      >
        Сбросить
      </Button>
    ) : null;

  /**
   * Панель выбора: цвет + поиск + сетка иконок.
   * Сетка иконок всегда ограничена по высоте и прокручивается внутри себя,
   * чтобы пикер не растягивал страницу/модалку. `scroll` = true в поповере
   * (`iconOnly`) даёт чуть более компактную высоту (240 vs 280 inline).
   */
  const panel = (scroll: boolean): JSX.Element => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: 'var(--color-text-secondary)', letterSpacing: 0.2, marginBottom: 6, paddingLeft: 2 }}>
        Цвет
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
        {spaceTilePalette.map((c) => {
          const active = (selectedColor ?? '').toLowerCase() === c.toLowerCase();
          return (
            <button
              key={c}
              type="button"
              aria-label={`Цвет ${c}`}
              onClick={() => onChange?.(buildSpaceIcon(selectedIconKey, c))}
              style={{
                width: 20,
                height: 20,
                borderRadius: 6,
                cursor: 'pointer',
                background: c,
                border: active ? '2px solid var(--color-text)' : '1px solid rgba(0,0,0,0.12)',
                boxShadow: active ? `0 0 0 2px var(--color-bg-container)` : 'none',
                outline: active ? `2px solid ${c}` : 'none',
              }}
            />
          );
        })}
      </div>
      <Input
        size="small"
        allowClear
        placeholder="Поиск иконки…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{ marginBottom: 10 }}
      />
      <div style={{ maxHeight: scroll ? 240 : 280, overflowY: 'auto', paddingRight: 4 }}>
        {groups.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', padding: '8px 2px' }}>
            Ничего не найдено
          </div>
        ) : null}
        {groups.map(({ group, items }) => (
          <div key={group} style={{ marginBottom: 10 }}>
            <div
              style={{
                fontSize: 10.5,
                color: 'var(--color-text-secondary)',
                letterSpacing: 0.2,
                marginBottom: 5,
                paddingLeft: 2,
              }}
            >
              {group}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {items.map((def) => {
                const active = selectedIconKey === def.key;
                return (
                  <Tooltip key={def.key} title={def.label}>
                    <button
                      type="button"
                      aria-label={def.label}
                      onClick={() => onChange?.(buildSpaceIcon(def.key, selectedColor))}
                      style={{
                        width: 30,
                        height: 30,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        borderRadius: 7,
                        color: active ? 'var(--color-primary)' : 'var(--color-text)',
                        background: active
                          ? 'var(--color-primary-bg)'
                          : 'var(--color-bg-container)',
                        border: active
                          ? '1px solid var(--color-primary)'
                          : '1px solid var(--color-border)',
                      }}
                    >
                      <def.Icon size={16} strokeWidth={1.9} />
                    </button>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  // Режим представлений/типов: компактная плитка-триггер + сетка в поповере.
  // Модалка остаётся короткой (своего скролла нет) — единственная полоса
  // прокрутки живёт внутри поповера.
  if (iconOnly) {
    const def = selectedIconKey ? getSpaceIconDef(selectedIconKey) : undefined;
    return (
      <span data-testid="space-icon-picker" style={{ flex: 'none', display: 'inline-flex' }}>
        <Popover
          trigger="click"
          placement="bottomLeft"
          styles={{ body: { padding: 12 } }}
          content={
            <div style={{ width: 320 }}>
              {panel(true)}
              {resetBtn ? <div style={{ marginTop: 4 }}>{resetBtn}</div> : null}
            </div>
          }
        >
          <button
            type="button"
            aria-label="Выбрать иконку"
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              border: '1px solid var(--color-border)',
              background: 'var(--color-bg-layout)',
              color: 'var(--color-text)',
            }}
          >
            {def ? (
              <def.Icon size={20} strokeWidth={1.9} color={selectedColor ?? 'currentColor'} />
            ) : (
              <span style={{ color: 'var(--color-text-secondary)', fontSize: 13 }}>—</span>
            )}
          </button>
        </Popover>
      </span>
    );
  }

  // Режим пространства: компактная плитка-аватар + панель (цвет + сетка) в
  // поповере, чтобы модалка создания пространства не растягивалась на весь
  // экран. Единственная полоса прокрутки живёт внутри сетки поповера.
  return (
    <div
      style={{ display: 'flex', gap: 12, alignItems: 'center' }}
      data-testid="space-icon-picker"
    >
      <Popover
        trigger="click"
        placement="bottomLeft"
        styles={{ body: { padding: 12 } }}
        content={
          <div style={{ width: 320 }}>
            {panel(true)}
          </div>
        }
      >
        <button
          type="button"
          aria-label="Выбрать иконку"
          style={{
            padding: 0,
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            lineHeight: 0,
            borderRadius: 12,
          }}
        >
          <SpaceAvatar name={name || 'Пространство'} icon={value} size={40} />
        </button>
      </Popover>
      {resetBtn}
    </div>
  );
}
